import React, { useMemo, memo, CSSProperties, ReactElement, useEffect } from "react";
import { Task } from "../types";
import { Menu } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  ArrowUturnLeftIcon,
  TrashIcon,
  TagIcon,
  CalendarIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { CATEGORY_COLORS } from "../services/database";
import { List, useDynamicRowHeight } from "react-window";
import { usePostHog } from 'posthog-js/react';

interface CompletedTasksViewProps {
  tasks: Task[];
  onRestore: (taskId: number) => void | Promise<void>;
  onDelete: (taskId: number) => void | Promise<void>;
}

interface BucketedTask {
  task: Task;
  completedDate: Date | null;
}

type VirtualItem =
  | { type: "header"; key: string; title: string }
  | { type: "task"; key: string; bucketed: BucketedTask };

interface VirtualRowProps {
  virtualItems: VirtualItem[];
  onRestore: (taskId: number) => void | Promise<void>;
  onDelete: (taskId: number) => void | Promise<void>;
}

// Row component for react-window v2 API
const VirtualRow = ({
  index,
  style,
  virtualItems,
  onRestore,
  onDelete,
}: {
  ariaAttributes: { "aria-posinset": number; "aria-setsize": number; role: "listitem" };
  index: number;
  style: CSSProperties;
} & VirtualRowProps): ReactElement => {
  const item = virtualItems[index];
  if (item.type === "header") {
    return (
      <div style={style} className="pt-2 pb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-secondary mb-2">
          {item.title}
        </h4>
      </div>
    );
  }
  return (
    <div style={style} className="pb-2">
      <CompletedTaskRow
        task={item.bucketed.task}
        completedDate={item.bucketed.completedDate}
        onRestore={onRestore}
        onDelete={onDelete}
      />
    </div>
  );
};

const CompletedTasksView: React.FC<CompletedTasksViewProps> = ({
  tasks,
  onRestore,
  onDelete,
}) => {
  const posthog = usePostHog();
  
  // Track completed tasks view on mount
  useEffect(() => {
    posthog?.capture('completed_tasks_viewed', {
      total_completed: tasks.length
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only track on mount, not when tasks change
  
  const { today, pastWeek, older } = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const sevenDaysAgo = new Date(startOfToday);
    sevenDaysAgo.setDate(startOfToday.getDate() - 7);

    const parsed: BucketedTask[] = tasks
      .map((task) => {
        if (!task.completedAt) {
          return { task, completedDate: null };
        }
        const date = new Date(task.completedAt);
        return {
          task,
          completedDate: Number.isNaN(date.getTime()) ? null : date,
        };
      })
      .sort((a, b) => {
        const aTime = a.completedDate ? a.completedDate.getTime() : 0;
        const bTime = b.completedDate ? b.completedDate.getTime() : 0;
        return bTime - aTime;
      });

    const todayBucket: BucketedTask[] = [];
    const pastWeekBucket: BucketedTask[] = [];
    const olderBucket: BucketedTask[] = [];

    parsed.forEach((item) => {
      if (!item.completedDate) {
        olderBucket.push(item);
        return;
      }

      if (item.completedDate >= startOfToday) {
        todayBucket.push(item);
      } else if (item.completedDate >= sevenDaysAgo) {
        pastWeekBucket.push(item);
      } else {
        olderBucket.push(item);
      }
    });

    return {
      today: todayBucket,
      pastWeek: pastWeekBucket,
      older: olderBucket,
    };
  }, [tasks]);

  const virtualItems: VirtualItem[] = useMemo(() => {
    const items: VirtualItem[] = [];
    const pushSection = (title: string, list: BucketedTask[]) => {
      if (!list.length) return;
      items.push({ type: "header", key: `${title}-header`, title });
      list.forEach((entry) =>
        items.push({
          type: "task",
          key: `task-${entry.task.id}`,
          bucketed: entry,
        })
      );
    };

    pushSection("Today", today);
    pushSection("Past 7 days", pastWeek);
    pushSection("Older", older);
    return items;
  }, [today, pastWeek, older]);

  // Dynamic row heights (react-window v2)
  const ESTIMATED_ROW_HEIGHT = 96; // reasonable average for a task card
  const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: ESTIMATED_ROW_HEIGHT });

  const listHeight = useMemo(() => {
    const totalEstimated = virtualItems.length * ESTIMATED_ROW_HEIGHT;
    // Let the list grow to fit content; keep a sensible minimum, no hard cap.
    return Math.max(totalEstimated, 320);
  }, [virtualItems]);

  const rowProps: VirtualRowProps = useMemo(
    () => ({ virtualItems, onRestore, onDelete }),
    [virtualItems, onRestore, onDelete]
  );

  if (tasks.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-center">
        <p className="text-sm text-gray-500 dark:text-dark-secondary">
          No tasks completed yet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <List
        rowComponent={VirtualRow}
        rowProps={rowProps}
        rowCount={virtualItems.length}
        rowHeight={dynamicRowHeight}
        overscanCount={10}
        style={{ height: listHeight, width: "100%" }}
      />
    </div>
  );
};

interface CompletedTaskRowProps {
  task: Task;
  completedDate: Date | null;
  onRestore: (taskId: number) => void | Promise<void>;
  onDelete: (taskId: number) => void | Promise<void>;
}

const CompletedTaskRowBase: React.FC<CompletedTaskRowProps> = ({
  task,
  completedDate,
  onRestore,
  onDelete,
}) => {
  const categoryStyle = getCategoryStyle(task.category);
  const formattedCompletedAt = formatCompletedAt(completedDate);

  return (
    <div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-base font-medium text-gray-900 dark:text-dark-primary break-words">
            {task.text}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-dark-secondary">
            <span className="inline-flex items-center">
              <CheckCircleIcon className="mr-1 h-4 w-4 text-green-700 dark:text-green-300" />
              {formattedCompletedAt}
            </span>
            {task.timeSpent > 0 && (
              <span className="inline-flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {formatTimeSpent(task.timeSpent)}
              </span>
            )}
            {task.category && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded ${categoryStyle.bg} ${categoryStyle.text}`}
              >
                <TagIcon className="h-3 w-3 mr-1" />
                {task.category}
              </span>
            )}
            {task.dueDate && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded ${getDueDateStyle(task.dueDate).bg} ${getDueDateStyle(task.dueDate).text}`}
              >
                <CalendarIcon className="h-3 w-3 mr-1" />
                {formatDueDate(task.dueDate)}
              </span>
            )}
          </div>
        </div>
        <Menu as="div" className="relative ml-3 inline-block text-left">
          <Menu.Button className="rounded-full p-1 text-gray-400 hover:text-gray-600 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-50 dark:hover:bg-dark-hover focus:outline-none">
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          <Menu.Items 
            anchor="bottom end"
            transition
            className="w-48 rounded-md bg-white dark:bg-dark-card shadow-lg ring-1 ring-black ring-opacity-10 dark:ring-white dark:ring-opacity-20 focus:outline-none z-[9999] origin-top-right transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onRestore(task.id)}
                    className={`whitespace-nowrap ${
                      active
                        ? "bg-gray-100 dark:bg-dark-hover text-gray-900 dark:text-dark-primary"
                        : "text-gray-700 dark:text-gray-200"
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <ArrowUturnLeftIcon className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-300" />
                    Restore to Upcoming
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onDelete(task.id)}
                    className={`${
                      active
                        ? "bg-gray-100 dark:bg-dark-hover text-red-600 dark:text-red-400"
                        : "text-red-600 dark:text-red-400"
                    } group flex w-full items-center px-4 py-2 text-sm`}
                  >
                    <TrashIcon className="h-4 w-4 mr-3" />
                    Delete
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>
    </div>
  );
};

const CompletedTaskRow = memo(CompletedTaskRowBase);

const formatCompletedAt = (date: Date | null): string => {
  if (!date) {
    return "Completion time unavailable";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatTimeSpent = (seconds: number): string => {
  if (!seconds || isNaN(seconds) || seconds <= 0) return "";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return minutes > 0 ? `${minutes}m` : "< 1m";
};

const getCategoryStyle = (category?: string) => {
  if (!category) {
    return {
      bg: "bg-gray-100 dark:bg-gray-900/20",
      text: "text-gray-700 dark:text-gray-300",
    };
  }

  const color = CATEGORY_COLORS[category] || "gray";
  const bgClasses: Record<string, string> = {
    blue: "bg-blue-100 dark:bg-blue-900/20",
    green: "bg-green-100 dark:bg-green-900/20",
    red: "bg-red-100 dark:bg-red-900/20",
    purple: "bg-purple-100 dark:bg-purple-900/20",
    yellow: "bg-yellow-100 dark:bg-yellow-900/20",
    gray: "bg-gray-100 dark:bg-gray-900/20",
  };
  const textClasses: Record<string, string> = {
    blue: "text-blue-700 dark:text-blue-300",
    green: "text-green-700 dark:text-green-300",
    red: "text-red-700 dark:text-red-300",
    purple: "text-purple-700 dark:text-purple-300",
    yellow: "text-yellow-700 dark:text-yellow-300",
    gray: "text-gray-700 dark:text-gray-300",
  };

  return {
    bg: bgClasses[color] || bgClasses.gray,
    text: textClasses[color] || textClasses.gray,
  };
};

const getDueDateStyle = (dueDate: Date) => {
  const today = new Date();
  const due = new Date(dueDate);

  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  if (due < today) {
    return {
      bg: "bg-red-100 dark:bg-red-900/20",
      text: "text-red-700 dark:text-red-300",
      isColored: true,
    };
  }

  if (due.getTime() === today.getTime()) {
    return {
      bg: "bg-yellow-100 dark:bg-yellow-900/20",
      text: "text-yellow-700 dark:text-yellow-300",
      isColored: true,
    };
  }

  return {
    bg: "bg-gray-100 dark:bg-gray-900/20",
    text: "text-gray-700 dark:text-gray-300",
    isColored: false,
  };
};

const formatDueDate = (dueDate: Date): string => {
  const currentYear = new Date().getFullYear();
  const dueYear = dueDate.getFullYear();

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    ...(currentYear !== dueYear ? { year: "numeric" } : {}),
  };

  return dueDate.toLocaleDateString(undefined, options);
};

export default CompletedTasksView;

